import type { Except } from "type-fest";
import { z } from "zod";
import { nowIsoAndUnix } from "../../date-time/iso-and-unix";
import { pdfContentType } from "../content-types";
import type { StorageItem } from "../storage-item";
import { storageItemBaseZodSchema } from "../storage-item-base";
import type {
    StorageItemPdfId} from "./storage-item-pdf-id";
import {
    storageItemPdfIdFactory,
    storageItemPdfIdZodSchema,
} from "./storage-item-pdf-id";

const storageItemPdfZodSchema = storageItemBaseZodSchema.merge(
    z.object({
        _id: storageItemPdfIdZodSchema,
        type: z.literal("PDF"),
        contentType: z.literal(pdfContentType),
        blurhash: z.string().optional(),
    }),
);
type StorageItemPdf = z.infer<typeof storageItemPdfZodSchema>;

type StorageItemPdfFactoryParams = Except<
    StorageItemPdf,
    "_id" | "type" | "_type" | "_createdAt"
> & {
    _id?: StorageItemPdfId;
};

const storageItemPdfFactory = (
    params: Except<StorageItemPdfFactoryParams, "contentType">,
): StorageItemPdf => {
    return {
        _id: params._id ?? storageItemPdfIdFactory(),
        _type: "STORAGE_ITEM",
        type: "PDF",
        _createdAt: nowIsoAndUnix(),
        contentType: "application/pdf",
        ...params,
    };
};

const isStorageItemPdf = (
    storageItem:
        | { _type: StorageItem["_type"]; type: StorageItem["type"] }
        | undefined,
): storageItem is StorageItemPdf => {
    if (typeof storageItem === "undefined") return false;
    if (storageItem._type !== "STORAGE_ITEM") return false;
    return storageItem.type === "PDF";
};

export { isStorageItemPdf, storageItemPdfFactory, storageItemPdfZodSchema };
export type { StorageItemPdf };
